import type { Tier } from '@/components/ui/TierPill'
import { planFromTier } from '@/features/account/api/accountService'
import type { PlanId } from '@/features/onboarding/store'
import type { Plan } from '@/schemas/account'

/**
 * The one place `free | plus | pro` becomes a word a member reads.
 *
 * Three screens were each carrying their own
 * `plan === 'plus' ? 'Sadasya' : plan === 'pro' ? 'Guru' : 'Free'`, which is
 * how the Profile card and the Subscription banner end up disagreeing about
 * what someone is on. `Tier` is `TierPill`'s own union, so a label from here
 * can be handed straight to the pill with no cast.
 */
export const tierOf = (plan: Plan): Tier =>
  plan === 'plus' ? 'Sadasya' : plan === 'pro' ? 'Guru' : 'Free'

/**
 * A wire tier (`free | bull | shark`, as `subscriptions/` sends it) as the
 * word a member reads — `tierOf` after `planFromTier`, so an unknown tier
 * reads Free here exactly as it does in the chrome.
 */
export const tierName = (tier: string): Tier => tierOf(planFromTier(tier))

/**
 * A `plan_title` (`me/`, `confirm/`, `payments/` send the legacy TITLE —
 * "Bull", "Shark") as the word a member reads, or null when it names no tier
 * the app knows. Null, not Free: a payment row for an unknown plan must not
 * claim it bought Free. Only the fallback — prefer joining `plan_id` to the
 * catalogue and naming its tier.
 */
export function planTitleName(title: string): Tier | null {
  const tier = title.trim().toLowerCase()
  return tier === 'free' || tier === 'bull' || tier === 'shark' ? tierName(tier) : null
}

/**
 * The same plan as the onboarding flow's `PlanId`.
 *
 * The chrome and the pricing table were built from different vocabularies —
 * `plus`/`pro` on the wire, `sadasya`/`guru` in the plan cards — and the
 * Subscription screen is where the two meet.
 */
export const planIdOf = (plan: Plan): PlanId =>
  plan === 'plus' ? 'sadasya' : plan === 'pro' ? 'guru' : 'free'

/**
 * The upgrade the hub's banner offers, or null to show none.
 *
 * Only a Free member ever sees one: V2 draws "Get more with Sadasya" on the
 * Free frame (`4236:71539`) and NO banner at all on either paid tier's own
 * frame (`4236:75370` Sadasya, `4236:77013` Guru) — a member already paying
 * gets nothing to upgrade to here, even though Guru sits above Sadasya
 * (product decision D30, `DECISIONS.md`). A Sadasya→Guru nudge can ship later
 * if a frame ever draws one.
 */
export const upgradeFrom = (plan: Plan): 'Sadasya' | null => (plan === 'free' ? 'Sadasya' : null)
